// cmsImageCaptions — the Alpine controller for the CMS editor's "Images" surface.
//
// Unlike the generic form sections, this surface owns its WHOLE main area: a
// scrollable list of every property image with per-image caption + alt fields
// (left), and a lightbox-style preview of the selected image (right). The two
// panes share one component scope so `selectedId` + the live caption/alt values
// are plain reactive state — no cross-component event plumbing.
//
// Why a local preview instead of the render sidecar (which the form sections
// use): captions are a per-image concern with no rendered page section, so the
// sidecar has nothing meaningful to render (it fell back to the hero section).
// The image URLs + caption/alt are already client-side, so the preview is a
// reactive `<img>` + text — instant, no render round-trip, and it mirrors how a
// guest sees the image in the site's lightbox.
//
// Autosave is unchanged: editing a field dispatches the same bubbling
// `cms-caption-changed` event the editor controller (editor.ts) listens for on
// #cms-editor-main; this controller is purely the view/selection layer.

interface CaptionImage {
  id: string;
  url?: string;
  caption?: string;
  alt?: string;
  // Precomputed lowercased server-built search blob (BuildCMSImageList):
  // caption + alt + scene metadata + EN/DE enum synonyms.
  search?: string;
  // media-04: "too_large" (server overlay) → the templ renders a placeholder
  // instead of the raw url; width/height feed its size label.
  processing_error?: string;
  width?: number;
  height?: number;
}

interface CMSImageCaptionsConfig {
  images: CaptionImage[];
  editable: boolean;
}

export function cmsImageCaptions(config: CMSImageCaptionsConfig) {
  return {
    images: (config.images || []) as CaptionImage[],
    editable: !!config.editable,
    q: "",
    // Seed the preview with the first image so the pane is never empty on open.
    selectedId: (config.images && config.images[0]?.id) || "",

    filtered(): CaptionImage[] {
      const q = this.q.trim().toLowerCase();
      if (!q) return this.images;
      // Match the precomputed server blob (language-agnostic); fall back to
      // per-field matching for any entry lacking it (legacy/raw).
      return this.images.filter((im) =>
        im.search !== undefined
          ? im.search.includes(q)
          : [im.caption, im.alt].some((s) => (s || "").toLowerCase().includes(q)),
      );
    },
    selected(): CaptionImage | null {
      return this.images.find((im) => im.id === this.selectedId) || null;
    },
    select(id: string) {
      this.selectedId = id;
    },
    // Persist an edit by dispatching the event the editor controller debounces
    // + flushes. x-model has already updated the in-memory value (driving the
    // live preview); we dispatch the raw input value so autosave never depends
    // on x-model/@input listener ordering. No-op on a non-primary locale.
    onEdit(im: CaptionImage, field: "caption" | "alt", value: string) {
      if (!this.editable) return;
      (this as any).$dispatch("cms-caption-changed", { imageId: im.id, field, value });
    },
  };
}
